import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Phone, Mail, MessageCircle, Tag, X, Loader2 } from "lucide-react";
import { Modal } from "../primitives/Modal";
import { useChainStore, cfBus } from "../../data/chainStore";
import { apiError, apiErrorCode, apiErrorField } from "../../lib/api";
import { useCanManageContacts } from "../../hooks/useDealAccess";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumSelect } from "../brand/PremiumSelect";
import { PremiumSegmented } from "../brand/PremiumSegmented";
import { PremiumTextarea } from "../brand/PremiumTextarea";

const ROLES = ["Seller", "Buyer", "Estate Agent", "Managing Agent", "Solicitor", "Mortgage Broker", "Surveyor"];
const PREFS = [
  { id: "Email", icon: Mail },
  { id: "Phone", icon: Phone },
  { id: "WhatsApp", icon: MessageCircle },
];

const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "·";

const fullName = (f) => `${(f.first_name || "").trim()} ${(f.last_name || "").trim()}`.trim();

const validate = (f) => {
  const e = {};
  if (!f.first_name?.trim()) e.first_name = "Required";
  if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Invalid email";
  if (!f.phone?.trim()) {
    e.phone = "Required";
  } else {
    const s = f.phone.trim();
    const digits = s.replace(/\D/g, "");
    if (digits.length < 7) {
      e.phone = "Looks too short — include country code (e.g. +44 7700 900000).";
    } else if (!s.startsWith("+") && digits.length < 10) {
      e.phone = "Include country code (e.g. +44 7700 900000).";
    }
  }
  if (!f.role) e.role = "Required";
  return e;
};

const TagInput = ({ tags, onChange }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setDraft("");
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-lg border border-slate-200 bg-white focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[11px] font-semibold border border-indigo-100">
          <Tag size={9} /> {t}
          <button onClick={() => onChange(tags.filter((x) => x !== t))} className="ml-0.5 hover:text-indigo-900" aria-label={`Remove ${t}`}>
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder={tags.length ? "" : "Type a tag and press Enter…"}
        className="flex-1 min-w-[120px] text-[12.5px] outline-none bg-transparent py-1"
        data-testid="contact-tag-input"
      />
    </div>
  );
};

const splitName = (name) => {
  const parts = (name || "").trim().split(/\s+/);
  return { first_name: parts[0] || "", last_name: parts.slice(1).join(" ") };
};

const initialForm = (preset = {}) => {
  // Accept preset.first_name/last_name OR legacy preset.name
  const fromName = preset?.name && !preset?.first_name ? splitName(preset.name) : {};
  return {
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    company: "",
    role: "",
    notes: "",
    tags: [],
    pref: "Email",
    status: "Active",
    ...preset,
    ...fromName,
  };
};

export const AddContactModal = () => {
  const { addContact, updateContact, contactStatuses } = useChainStore();
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState({});
  const [form, setForm] = useState(initialForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isEditing = Boolean(preset?._editing && preset?.id);
  const canManage = useCanManageContacts();

  useEffect(() => {
    return cfBus.on("cf:open-contact", (p) => {
      if (!canManage) return;
      setPreset(p || {});
      setForm(initialForm(p));
      setErrors({});
      setSaving(false);
      setSaved(false);
      setOpen(true);
    });
  }, [canManage]);

  const close = () => { if (!saving) setOpen(false); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (saving) return;
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) {
      const firstField = Object.keys(e)[0];
      toast.error("Missing required fields", { description: `Check ${Object.keys(e).join(", ")}.` });
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-testid="contact-${firstField.replace("_", "-")}"]`);
        el?.focus();
      });
      return;
    }
    setSaving(true);
    try {
      let savedRow;
      const payload = {
        first_name: form.first_name.trim(),
        last_name: (form.last_name || "").trim(),
        phone: form.phone.trim(),
        email: form.email?.trim() || null,
        company: form.company?.trim() || null,
        role: form.role,
        notes: form.notes || null,
        tags: form.tags || [],
        pref: form.pref || "Email",
        status: form.status || "Active",
      };
      if (isEditing) {
        savedRow = await updateContact(preset.id, payload);
      } else {
        savedRow = await addContact(payload);
      }
      setSaved(true);
      setTimeout(() => {
        setOpen(false);
        setSaving(false);
        toast.success(`${savedRow.name} ${isEditing ? "updated" : "saved"}`, {
          description: `${savedRow.role}${savedRow.company ? ` · ${savedRow.company}` : ""}`,
        });
        preset.onSaved?.(savedRow);
      }, 600);
    } catch (err) {
      setSaving(false);
      const msg = apiError(err);
      const code = apiErrorCode(err);
      const field = apiErrorField(err);

      // Inline-highlight the offending field whenever the server told us which one.
      // Keeps the operator's eye on the exact thing that needs fixing — no toast-only failures.
      if (field && typeof field === "string") {
        setErrors((prev) => ({ ...prev, [field]: msg }));
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-testid="contact-${field.replace(/_/g, "-")}"]`);
          if (el && typeof el.focus === "function") el.focus();
        });
      }

      // Title reflects the failure mode so the toast itself reads operationally.
      let title;
      if (code === "duplicate_email") title = "Email already in use";
      else if (code === "workspace_missing") title = "Workspace not set";
      else if (code === "contact_create_failed" || code === "contact_update_failed") title = "Save failed";
      else if (err?.response?.status === 422) title = "Some fields need attention";
      else if (err?.response?.status === 401) title = "Session expired";
      else if (err?.response?.status === 403) title = "Not permitted";
      else if (err?.response?.status === 429) title = "Slow down";
      else title = isEditing ? "Could not save changes" : "Could not save contact";

      toast.error(title, { description: msg });
    }
  };

  const previewName = fullName(form) || "New contact";

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEditing ? "Edit contact" : "Add a new contact"}
      eyebrow="Contacts"
      testid="add-contact-modal"
      width={620}
      footer={
        <>
          <button onClick={close} className="btn btn-ghost" disabled={saving} data-testid="contact-cancel">Cancel</button>
          <button onClick={submit} disabled={saving || saved} className="btn btn-primary" data-testid="contact-save">
            {saved ? (
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="cf-pulse" /> Saved</span>
            ) : saving ? (
              <span className="inline-flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Saving…</span>
            ) : (isEditing ? "Save changes" : "Save contact")}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-12 gap-4">
        {/* Avatar preview */}
        <div className="col-span-12 flex items-center gap-4 p-3 rounded-2xl bg-indigo-50/60 border border-indigo-100">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white font-display font-bold text-[18px] flex items-center justify-center cf-shadow-md">
            {initialsOf(previewName)}
          </div>
          <div>
            <p className="text-[13px] font-semibold text-slate-900" data-testid="contact-preview-name">{previewName}</p>
            <p className="text-[11.5px] text-slate-500">{form.role || "Role"} · {form.company || "Company"}</p>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6">
          <PremiumInput label="First name" required value={form.first_name} onChange={(v) => set("first_name", v)} placeholder="Eleanor" error={errors.first_name} autoFocus testid="contact-first-name" />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput label="Last name" value={form.last_name} onChange={(v) => set("last_name", v)} placeholder="Whitfield" testid="contact-last-name" />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput label="Mobile number" type="tel" required value={form.phone} onChange={(v) => set("phone", v)} placeholder="+44 7700 900000" error={errors.phone} autoComplete="tel" testid="contact-phone" />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput label="Email address" type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="name@email.com" error={errors.email} autoComplete="email" clearable testid="contact-email" />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput label="Company" value={form.company} onChange={(v) => set("company", v)} placeholder="Brabners LLP" testid="contact-company" />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Role" value={form.role} onChange={(v) => set("role", v)} placeholder="Select role…" error={errors.role} testid="contact-role"
            options={ROLES.map((r) => ({ value: r, label: r }))} />
        </div>
        <div className="col-span-12">
          <PremiumSelect label="Status" value={form.status} onChange={(v) => set("status", v)} testid="contact-status"
            options={(contactStatuses || ["Active", "Pending", "Archived"]).map((s) => ({ value: s, label: s }))} />
        </div>
        <div className="col-span-12">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Preferred communication</p>
          <PremiumSegmented
            value={form.pref}
            onChange={(v) => set("pref", v)}
            size="sm"
            ariaLabel="Preferred communication"
            testid="contact-pref"
            options={PREFS.map((p) => ({ value: p.id, label: p.id }))}
          />
        </div>
        <div className="col-span-12">
          <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Tags</label>
          <TagInput tags={form.tags} onChange={(t) => set("tags", t)} />
          <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">Press Enter to add</p>
        </div>
        <div className="col-span-12">
          <PremiumTextarea label="Notes" value={form.notes} onChange={(v) => set("notes", v)} placeholder="Anything the team should know about this contact…" rows={3} autoResize maxRows={6} testid="contact-notes" />
        </div>
      </div>
    </Modal>
  );
};

export { initialsOf };
